import React, { useState } from 'react';
import { ChevronDown, HelpCircle } from 'lucide-react'; // Optional icons

const FaqItem = ({ question, answer, isOpen, onClick }) => {
  return (
    <div className="border-b border-gray-200 dark:border-gray-700 py-6">
      <h3> {/* Changed from dt to h3 for better SEO for individual questions */}
        <button
          onClick={onClick}
          aria-expanded={isOpen}
          aria-controls={`faq-answer-${question.replace(/\s+/g, '-')}`}
          className="flex justify-between items-center w-full text-left text-lg font-medium text-gray-900 dark:text-gray-100 hover:text-blue-600 dark:hover:text-blue-400 focus:outline-none focus-visible:ring focus-visible:ring-blue-500 focus-visible:ring-opacity-75"
        >
          <span>{question}</span>
          <ChevronDown
            className={`h-5 w-5 transform transition-transform duration-200 ${
              isOpen ? 'rotate-180' : ''
            }`}
          />
        </button>
      </h3>
      <div
        id={`faq-answer-${question.replace(/\s+/g, '-')}`}
        className={`mt-3 text-gray-700 dark:text-gray-300 transition-all duration-300 ease-in-out overflow-hidden ${
          isOpen ? 'max-h-screen opacity-100' : 'max-h-0 opacity-0'
        }`}
        style={{ transitionProperty: 'max-height, opacity' }} // For smoother transition
      >
        <div className="prose prose-sm sm:prose dark:prose-invert max-w-none leading-relaxed pb-2">
            {/* The 'prose' classes from Tailwind Typography are great for long-form text */}
            {answer}
        </div>
      </div>
    </div>
  );
};

const FAQPage = () => {
  const [openAccordionId, setOpenAccordionId] = useState(null);

  const toggleAccordion = (id) => {
    setOpenAccordionId(openAccordionId === id ? null : id);
  };

  // Define your FAQ data here. Use JSX for answers to include links, bolding, etc.
  const faqData = [
    {
      id: 'what-is-tabdivider',
      question: 'What is TabDivider?',
      answer: (
        <p>
          TabDivider is a powerful and user-friendly <strong>bill splitting app</strong> designed to make sharing expenses with friends, family, or colleagues effortless. You can easily input items from receipts, assign them to different people, and calculate who owes whom, including handling taxes, service charges, and tips.
        </p>
      ),
    },
    {
      id: 'how-ocr-works',
      question: 'How does the OCR (receipt scanning) feature work?',
      answer: (
        <p>
          TabDivider uses Optical Character Recognition (OCR) technology to automatically extract item names and prices from an uploaded image of your receipt. Simply take a photo or upload an existing image, and our system will attempt to parse the text.
          <br />
          <strong>Tips for best OCR results:</strong>
          <ul className="list-disc pl-5 mt-2">
            <li>Use a clear, well-lit photo.</li>
            <li>Ensure the receipt is flat and not crumpled.</li>
            <li>Crop the image to show only the receipt.</li>
            <li>Avoid shadows or glare on the receipt.</li>
          </ul>
          Always review the extracted information, as OCR is not always 100% accurate and you might need to make manual adjustments.
        </p>
      ),
    },
    {
      id: 'edit-ocr-results',
      question: 'What if the OCR results are inaccurate?',
      answer: (
        <p>
          If the OCR doesn't perfectly capture all items or prices, don't worry! TabDivider allows you to easily <strong>edit, add, or delete items</strong> after the scan. You can correct names, adjust prices, and add any missed items manually to ensure the bill is accurate.
        </p>
      ),
    },
    {
      id: 'multiple-receipts',
      question: 'Can I manage multiple receipts for one bill or event?',
      answer: (
        <p>
          Yes! TabDivider allows you to add and manage <strong>multiple receipts</strong> within a single bill session. This is perfect for situations where expenses are incurred over several transactions (e.g., a weekend trip with friends). Each receipt can have its own items, charges, and designated payer.
        </p>
      ),
    },
    {
      id: 'splitting-methods',
      question: 'What bill splitting methods does TabDivider support?',
      answer: (
        <>
          <p>TabDivider offers flexible splitting options for each receipt:</p>
          <ul className="list-disc pl-5 mt-2">
            <li>
              <strong>Even Split:</strong> The total amount of the receipt (including all items, taxes, and charges) is divided equally among all selected participants for that receipt.
            </li>
            <li>
              <strong>Split by Item:</strong> You can assign specific items (or quantities of an item) to individual people. Taxes and other overall receipt charges (like service fees or tips) are then distributed proportionally based on each person's share of the item subtotal.
            </li>
          </ul>
        </>
      ),
    },
    {
      id: 'how-charges-split',
      question: 'How are taxes, service charges, and tips split?',
      answer: (
        <p>
          When splitting <strong>by item</strong>, common charges like taxes, service fees, and tips applied to the whole receipt are distributed proportionally. This means if someone's items make up 30% of the subtotal, they'll also pay 30% of these additional charges. If splitting <strong>evenly</strong>, all charges are simply included in the total amount that gets divided equally. You can input these charges as fixed amounts or percentages.
        </p>
      ),
    },
    {
      id: 'adding-people',
      question: 'How do I add people to a bill?',
      answer: (
        <p>
          You can easily add participants by clicking the "Add Person" button. You can then name each person. For each receipt, you'll also specify who paid for it.
        </p>
      ),
    },
    {
      id: 'currency-region',
      question: 'Can I change the currency and region?',
      answer: (
        <p>
          Yes, TabDivider allows you to select your preferred <strong>currency</strong> for calculations. You can also set your <strong>region</strong>, which helps in correctly labeling charges like "Tax" (e.g., VAT, GST) or "Service Charge" according to local conventions. These settings can usually be found in the app's main settings or header.
        </p>
      ),
    },
    {
      id: 'sharing-bill',
      question: 'How do I share a bill with others?',
      answer: (
        <p>
          TabDivider allows you to generate a <strong>shareable link</strong> for your bill. When you click "Share Bill," the current state of your bill is saved, and a unique URL is created. Anyone with this link can view the bill details, items, and settlement plan. This makes it easy to show everyone the breakdown without them needing an account.
        </p>
      ),
    },
    {
      id: 'data-privacy',
      question: 'Is my bill data private when I share it?',
      answer: (
        <p>
          When you share a bill, the data is stored securely (using Supabase as our backend). The bill is accessible to anyone who has the unique shareable link. We recommend only sharing these links with trusted individuals involved in the bill. We do not require accounts to view shared bills, for ease of use.
        </p>
      ),
    },
    {
      id: 'download-summary',
      question: 'Can I download a summary of the bill?',
      answer: (
        <p>
          Yes! TabDivider provides a "Download PDF" feature. This generates a comprehensive PDF document summarizing all receipts, items, individual shares, and the final settlement plan. It's a great way to keep a record or share a formal breakdown.
        </p>
      ),
    },
    {
      id: 'settlement-plan',
      question: 'How does the settlement plan work?',
      answer: (
        <p>
          After all expenses are entered and assigned, TabDivider calculates the most efficient way for everyone to settle up. It determines who owes money and who should receive money, minimizing the number of transactions needed. For example, instead of Person A paying Person B and Person B paying Person C, it might calculate that Person A just needs to pay Person C directly.
        </p>
      ),
    },
     {
      id: 'still-questions',
      question: 'I have more questions or need help!',
      answer: (
        <p>
          We're here to help! If your question isn't answered here, please visit our{' '}
          <a href="/contact-us" className="text-blue-600 dark:text-blue-400 hover:underline font-medium">
            Contact Us page
          </a>{' '}
          to get in touch with our support team. We also appreciate any feedback you have to make TabDivider even better for managing shared expenses.
        </p>
      ),
    }
  ];

  return (
    <div className="bg-gray-50 dark:bg-gray-900 min-h-screen">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16 lg:py-20">
        <header className="text-center mb-10 sm:mb-16">
          <HelpCircle className="h-12 w-12 sm:h-16 sm:w-16 mx-auto mb-4 text-blue-600 dark:text-blue-400" />
          <h1 className="text-4xl sm:text-5xl font-extrabold text-gray-900 dark:text-white">
            Frequently Asked Questions
          </h1>
          <p className="mt-4 text-lg sm:text-xl text-gray-600 dark:text-gray-300">
            Find answers to common questions about TabDivider, your go-to <strong className="text-gray-700 dark:text-gray-200">bill splitting and expense sharing tool</strong>.
          </p>
        </header>

        <div className="bg-white dark:bg-gray-800 shadow-xl rounded-lg p-6 sm:p-8 lg:p-10">
          {/* Using dl/dt/dd for semantic accordion structure is an option,
              but h3/div is also common and can be slightly better for question SEO.
              The WAI-ARIA attributes are key for accessibility.
          */}
          {faqData.map((item) => (
            <FaqItem
              key={item.id}
              question={item.question}
              answer={item.answer}
              isOpen={openAccordionId === item.id}
              onClick={() => toggleAccordion(item.id)}
            />
          ))}
        </div>

        <section className="mt-12 sm:mt-16 text-center">
          <h2 className="text-2xl sm:text-3xl font-semibold text-gray-800 dark:text-gray-100 mb-4">
            Still have questions?
          </h2>
          <p className="text-lg text-gray-700 dark:text-gray-300 mb-6">
            If you can't find the answer you're looking for, please don't hesitate to reach out.
          </p>
          <a
            href="/contact-us" // Make sure this route exists
            className="inline-flex items-center justify-center px-6 py-3 border border-transparent text-base font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 dark:ring-offset-gray-900"
          >
            Contact TabDivider Support
          </a>
        </section>
      </div>
    </div>
  );
};

export default FAQPage;